<template>
    <component
        :is="props.is"
        :to="props.to"
        :target="props.target"
        :class="{
            [$style.cardContainer]: true,
            [$style.responsive]: props.responsive,
            [$style.bigCard]: effectiveAppearance === 'big_horizontal',
            [$style.icon]: effectiveAppearance === 'icon',
            [placeholderStyles.placeholder]: (!!effectiveSrc && !isLoaded) || props.placeholder,
        }"
    >
        <img
            v-if="effectiveSrc"
            :key="
                props.lazy
                    ? `${effectiveSrc}_${effectiveAppearance}`
                    : undefined /* remount the image if it is lazy, otherwise nobody update src */
            "
            data-allow-mismatch
            :alt="props.alt"
            v-bind="
                props.lazy
                    ? {
                          'data-src': effectiveSrc,
                          'data-sizes': 'auto',
                          class: [$style.img, 'lazyload'],
                          loading: 'lazy',
                      }
                    : {
                          src: effectiveSrc,
                          class: $style.img,
                          fetchpriority: 'high',
                          srcset: srcSet,
                      }
            "
            itemprop="image"
            @load="() => (isLoaded = true)"
        />
        <slot />
    </component>
</template>

<script setup lang="ts">
import { ReactiveHead, useHead } from '@unhead/vue'

import 'lazysizes'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import placeholderStyles from '@/components_new/AnimatedPlaceholder/AnimatedPlaceholder.module.css'
import BaseLink from '@/components_new/BaseLink.vue'
import { LinkTarget } from '@/types'
import '@/utils/lazysizes-pg-thumbs-plugin'
import { processThumbSrcset } from '@/utils/pg-thumbs-helpers'

export type CardAppearance = 'big_horizontal' | 'medium_horizontal' | 'icon'

export type AppearanceMediaCondition = {
    media: string
    appearance: CardAppearance
}

interface CardContainerProps {
    is?: 'div' | typeof BaseLink
    to?: RouteLocationRaw
    appearance?: CardAppearance
    approximateWidth?: number
    target?: LinkTarget
    src?: string
    alt?: string
    srcsetOn?: boolean
    responsive?: boolean
    lazy?: boolean
    preload?: boolean
    placeholder?: boolean
    /* appearance media and sources goes together and every element in array correspond each other */
    sources?: string[]
    appearanceMedia?: AppearanceMediaCondition[]
}

const props = withDefaults(defineProps<CardContainerProps>(), {
    is: 'div',
    appearance: 'medium_horizontal',
    srcsetOn: true,
    lazy: true,
})

const conditionalAppearance = ref<CardAppearance | undefined>()
const conditionalSrc = ref<string | undefined>()
const isLoaded = ref<boolean>(false)
const effectiveAppearance = computed(() => conditionalAppearance.value || props.appearance)
const effectiveSrc = computed(() => conditionalSrc.value || props.src)
const mediaMatchers = ref<
    Array<{ matcher?: MediaQueryList | undefined; appearance: CardAppearance; src?: string | undefined }>
>([])

function updateAppearance() {
    mediaMatchers.value.forEach(({ matcher, appearance, src }) => {
        if (matcher?.matches) {
            conditionalAppearance.value = appearance
            conditionalSrc.value = src
        }
    })
}

function initMatchers() {
    removeMatchers()
    if (props.appearanceMedia?.length && props.sources?.length) {
        props.appearanceMedia.forEach((item, index) => {
            const matcher = window.matchMedia(item.media)
            mediaMatchers.value.push({ matcher, appearance: item.appearance, src: props.sources![index] })
            matcher.addEventListener('change', () => updateAppearance())
        })
        updateAppearance()
    }
}

function removeMatchers() {
    if (mediaMatchers.value.length) {
        mediaMatchers.value.forEach(({ matcher }) => {
            matcher?.removeEventListener('change', () => updateAppearance())
        })
        mediaMatchers.value = []
    }
}

onMounted(() => {
    initMatchers()
})

onBeforeUnmount(() => {
    removeMatchers()
})

watch(
    () => props.sources,
    () => {
        initMatchers()
    },
)

const srcSet = computed(() => {
    if (props.srcsetOn && !props.lazy && effectiveSrc.value) {
        let approximateWidth = 160

        if (props.appearance === 'icon') {
            approximateWidth = 90
        }
        if (props.appearance === 'big_horizontal') {
            approximateWidth = approximateWidth * 2
        }
        if (props.approximateWidth) {
            approximateWidth = props.approximateWidth
        }
        return processThumbSrcset(effectiveSrc.value, approximateWidth)
    }
    return ''
})

const preloadHeadLink = computed(() => {
    const link: ReactiveHead['link'] = []
    if (props.preload && effectiveSrc.value) {
        link.push({
            rel: 'preload',
            as: 'image',
            href: effectiveSrc.value,
            imagesrcset: srcSet.value,
            fetchpriority: 'high',
        })
    }
    return {
        link,
    }
})

useHead(preloadHeadLink)
</script>

<style module>
.cardContainer {
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: 100%;
    grid-template-areas: 'main';
    background-color: var(--carbon-400);
    width: 230px;
    aspect-ratio: var(--thumbnail-aspect-ratio);
    border-radius: 12px;
    overflow: hidden;
    contain: strict;
}
.icon {
    aspect-ratio: 1;
}
.responsive {
    width: 100%;
    aspect-ratio: var(--thumbnail-aspect-ratio);
    height: auto;
}

.bigCard.responsive {
    --big-card-width: 320;
    --big-card-height: calc(320 / var(--thumbnail-aspect-ratio));

    aspect-ratio: calc(var(--big-card-width) + var(--cards-gap)) / calc(var(--big-card-height) + var(--cards-gap)); /* same as normal, but with additional 8px gaps */
}

.img {
    display: block;
    object-fit: cover;
    width: 100%;
    height: 100%;
    grid-area: main;
}

:global(.lazyload),
:global(.lazyloading) {
    opacity: 0;
}
:global(.lazyloaded) {
    opacity: 1;
    transition: opacity ease-out 0.1s;
}
</style>
